import React, { useState, useEffect, useContext } from 'react';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import Heading from 'components/common/heading/heading';
import SiteContext from 'components/shared/siteContext/siteContext';
import useAsyncEffect from 'use-async-effect';
import { useLocation } from 'hooks/router';
import BackofficeReportsService from 'services/backofficeReportsService';
import BackofficeReportsHelper from 'components/navigation/backofficeReports/backofficeReportsHelper';
import ErrorItemNotFound from 'components/app/errors/errorItemNotFound';
import { useHistory } from 'hooks/router';
import { createTimeout, cancelTimeout, isTimeoutCanceled } from 'utils/timeout';
import { ShowConfirmPopup, ShowNotifyPopup } from 'components/common/popup/popup';
import AppContext from 'contexts/appContext';
import Panel from 'components/common/panel/panel';
import Spinner from 'components/common/spinner/spinner';
import moment from 'moment';
import BackOfficeReportsTaskPageFilter from './backOfficeReportsTaskPageFilter';
import './backOfficeReportsTaskPage.css';

import image_pdf from 'assets/images/file-pdf.png';
import image_error from 'assets/images/file-error.png';

export default function BackofficeReportsTaskPage(props) {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const id = props.match.params.id;
  const [isRunning, setIsRunning] = useState(false);
  const [secondsUntilComplete, setSecondsUntilComplete] = useState(0);
  const [secondsSinceTimeChange, setSecondsSinceTimeChange] = useState(0);
  const [runs, setRuns] = useState([]);
  const [taskData, setTaskData] = useState();
  const [timeoutNum, setTimeoutNum] = useState();
  const [timeoutCount, setTimeoutCount] = useState(0);
  const location = useLocation();
  const history = useHistory();
  const [showMoreVisible, setShowMoreVisible] = useState(false);
  const [pageNextOffset, setPageNextOffset] = useState(0);
  const [filters, setFilters] = useState(null);
  const [filterPopupVisible, setFilterPopupVisible] = useState(false);
  const [dataNotFound, setDataNotFound] = useState(false);

  const initialTimeoutPeriod = 10000;
  const periodicTimeoutPeriod = 2000;
  const defaultPageSize = 10;

  useEffect(() => {
    setAppArea(AppContext.Areas.BackofficeReports, 'Task Details');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Backoffice Reports', href: '/BackofficeReports/Run' }, { name: 'Tasks', href: '/BackofficeReports/Tasks' }, { name: 'Task Details', selected: true }]);
  }, []);

  useAsyncEffect(async () => {
    const task = await BackofficeReportsService.API.getTask(id);
    if (task) {
      const configurations = await BackofficeReportsHelper.getReportConfigurations();
      for (let configuration of configurations) {
        if (task.configurationId === configuration.configurationId) {
          task.isReportConfigurationStandard = configuration.isStandard;
        }
      }
      setTaskData(task);
    }
    else {
      setDataNotFound(true);
    }
  }, [location]);

  useAsyncEffect(async () => {
    const taskRuns = await BackofficeReportsService.API.getTaskRuns({ taskId: id, pageOffset: 0, pageSize: defaultPageSize, filters: filters });
    setRuns(taskRuns);
    setShowMoreVisible(taskRuns.length === defaultPageSize);
    setPageNextOffset(taskRuns.length);
  }, [filters, location]);

  useEffect(() => {
    return () => {
      cancelTimeout(timeoutNum);
    };
  }, [timeoutNum]);

  const onShowMore = async () => {
    const taskRuns = await BackofficeReportsService.API.getTaskRuns({ taskId: id, pageOffset: pageNextOffset, pageSize: defaultPageSize, filters: filters });
    setShowMoreVisible(taskRuns.length === defaultPageSize);
    setPageNextOffset(pageNextOffset + defaultPageSize);
    setRuns(runs.concat(taskRuns));
  };

  const handleRunNowClicked = async () => {
    setIsRunning(true);
    const runId = await BackofficeReportsService.API.executeTaskNow(id);

    const startCheckingForDocumentDownload = async (timeoutId) => {
      const run = await BackofficeReportsService.API.getTaskRunStatus(id, runId);
      if (isTimeoutCanceled(timeoutId)) {
        return;
      }
      if (run.runStatus === 0) {
        setTimeoutNum(createTimeout(startCheckingForDocumentDownload, periodicTimeoutPeriod));
        setTimeoutCount(timeoutCount + 1);
        if (run.secondsUntilComplete === secondsUntilComplete) {
          setSecondsSinceTimeChange(secondsSinceTimeChange + (initialTimeoutPeriod / 1000));
        }
        else {
          setSecondsSinceTimeChange(0);
        }
        setSecondsUntilComplete(run.secondsUntilComplete);
      }
      else {
        setRuns([run, ...runs]);
        setIsRunning(false);
      }
    };

    const timeoutId = createTimeout(startCheckingForDocumentDownload, initialTimeoutPeriod);
    setTimeoutNum(timeoutId);
    setTimeoutCount(0);
    setSecondsSinceTimeChange(initialTimeoutPeriod / 1000);
    setSecondsUntilComplete(0);
  };

  const handleDeleteTaskClicked = async () => {
    ShowConfirmPopup({
      title: 'Confirm',
      message: <div className='py-2'>Are you sure you want to delete this task?</div>,
      acceptLabel: 'Yes, Delete',
      acceptIconClass: 'icons8-trash',
      acceptVariant: 'danger',
      declineLabel: 'No',
      declineIconClass: 'icons8-delete',
      declineVariant: 'default',
      onAccept: () => {
        BackofficeReportsService.API.disableTask(id).then(() => {
          history.replace('/BackofficeReports/Tasks');
          ShowNotifyPopup({
            title: 'Success',
            message: 'Task has been deleted',
            variant: 'success',
            yPosition: 'bottom',
            xPosition: 'center',
            seconds: 5,
          });
        });
      }
    });
  };

  const renderRows = (data) => {
    if (!data || data.length === 0) {
      if (isRunning) {
        return <RunProgressItem secondsUntilComplete={secondsUntilComplete - secondsSinceTimeChange} isCalculating={timeoutCount === 0} />;
      }
      return <div>No run data found.</div>;
    }
    return (
      <>
        {isRunning ? <RunProgressItem secondsUntilComplete={secondsUntilComplete - secondsSinceTimeChange} isCalculating={timeoutCount === 0} /> : null}
        {runs.map((item, index) => (item.runStatus < 0 ? RunErrorItem(item) : RunHistoryItem(item)))}
      </>
    );
  };

  if (dataNotFound) {
    return <ErrorItemNotFound />;
  }

  if (!taskData) {
    //Loading View
    return (
      <>
        <Heading />

        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-12 col-md-10 col-lg-5 col-xl-4'>
            <Panel>
              <Panel.SkeletonHeader width={200} />
              <Panel.SkeletonBody rows={7} rowHeight={32} rowMargin={14} />
            </Panel>
          </div>
          <div className='col-12 col-md-10 col-lg-6 col-xl-7'>
            <Panel>
              <Panel.SkeletonHeader width={200} />
              <Panel.SkeletonBody rows={4} rowHeight={70} rowMargin={16} />
            </Panel>
          </div>
        </div>
      </>
    );
  }

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <SiteContext.Button hierarchyId={taskData.hierarchyId} readOnly />
          <div className='flex-grow-1 d-flex justify-content-end'>
            <Inputs.Button variant='secondary' onClick={handleRunNowClicked} iconClass='icons8-start' label='Run Now' loading={isRunning} loadingLabel='Running...' />
            <Inputs.Button variant='info' href={`/BackofficeReports/Task/Edit/${id}`} iconClass='icons8-edit' label='Edit' />
            <Inputs.Button variant='danger' onClick={handleDeleteTaskClicked} iconClass='icons8-trash' label='Delete' />
          </div>
        </Heading>
      </Flow.Header>
      <Flow.Body>
        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-12 col-md-10 col-lg-5 col-xl-4'>
            <Panel>
              <Panel.Header>Task Details</Panel.Header>
              <Panel.Body>
                <div className='mb-2'>
                  <div><div className='form-label'>Description</div></div>
                  <div>{taskData.taskName}</div>
                </div>
                <div className='mb-2'>
                  <div><div className='form-label'>Report Type</div></div>
                  <div>{taskData.reportName}</div>
                </div>
                <div className='mb-2'>
                  <div><div className='form-label'>Report View</div></div>
                  <div>{taskData.configName}</div>
                  {/*<div className='subtle'>{taskData.isReportConfigurationStandard ? 'Standard Views' : 'My Site Reports'}</div>*/}
                </div>
                <div className='mb-2'>
                  <div><div className='form-label'>Report Period</div></div>
                  <div>{taskData.dateDescription}</div>
                  <div className='subtle'>{taskData.isRelativeDate ? 'Relative' : 'Absolute'}</div>
                </div>
                <div className='mb-2'>
                  <div><div className='form-label'>Schedule</div></div>
                  <div>{!taskData.isScheduled ? 'On Demand' : 'Scheduled'}</div>
                  {!taskData.isScheduled ? null : <div className='subtle'>{taskData.scheduler ? taskData.scheduler.scheduleDescription : null}</div>}
                </div>
                <div className='mb-2'>
                  <div><div className='form-label'>Output</div></div>
                  <div>{!taskData.isDistributed ? 'Preview' : 'Email'}</div>
                  {!taskData.isDistributed ? null : <div className='subtle'>{taskData.distributor ? taskData.distributor.subscriberCount : 0} recipients</div>}
                </div>
                <div className='status small'>
                  <div>Created {moment(taskData.created).format('dddd, MMMM D, YYYY h:mma')}</div>
                  <div>Modified {moment(taskData.modified).format('dddd, MMMM D, YYYY h:mma')}</div>
                </div>
              </Panel.Body>
            </Panel>
          </div>
          <div className='col-12 col-md-10 col-lg-6 col-xl-7'>
            <Panel>
              <Panel.Header tools={<Inputs.Button variant='hover-default' iconClass='icons8-filter' onClick={() => setFilterPopupVisible(true)} />}>
                Run History
              </Panel.Header>
              <Panel.Body>
                <ul className='list-group list-group-flush'>
                  {renderRows(runs)}
                </ul>
                {!showMoreVisible ? null : (
                  <div className='mt-3'>
                    <Inputs.Button variant='hover-default' block onClick={onShowMore} label='Show More' iconClass='icons8-down-arrow' />
                  </div>
                )}
              </Panel.Body>
            </Panel>

            <BackOfficeReportsTaskPageFilter
              filters={filters}
              show={filterPopupVisible}
              onClose={() => setFilterPopupVisible(false)}
              onChange={(filters) => {
                setFilters(filters);
                setFilterPopupVisible(false);
              }}
            />
          </div>
        </div>
      </Flow.Body>
    </Flow>
  );
}

function RunHistoryItem(taskRunData) {
  const onRowClicked = () => {
    if (taskRunData.fileURL && taskRunData.fileURL.length > 0) {
      window.open(taskRunData.fileURL, '_blank').focus();
      /*ShowMediaPopup({
        title: 'Daily Sales Report - 10/31/2019',
        content:
          <iframe src='{taskRunData.fileURL}' title='PDF' height='100%' width='100%' style={{ height: '100%', width: '100%', display: 'block', borderWidth: 0 }}>An error occured.</iframe>
      });*/
    }
    else {
      ShowNotifyPopup({
        title: 'Error',
        message: 'Report failed to open',
        variant: 'danger',
        yPosition: 'bottom',
        xPosition: 'center',
        seconds: 5,
      });
    }
  };

  return (
    <li className='list-group-item list-group-item-action lh-120' key={taskRunData.taskRunId} onClick={onRowClicked}>
      <div className='px-2'>
        <div className='d-flex justify-content-between align-items-center'>
          <div className='d-flex justify-content-start align-items-center'>
            <img src={image_pdf} alt='PDF' style={{ height: 28 }} />
            <div className='ml-2 d-inline-block align-middle'>
              <span className='text-link'>
                <span>{taskRunData.reportName}</span>
                <span className='small ml-1'>({taskRunData.configurationName})</span>
              </span>
              <div className='text-link my-1'>
                <span>{taskRunData.requestedDateStr}</span>
                <span className='small ml-1'>
                  {(taskRunData.reportDateStr && taskRunData.reportDateStr.length > 0 && taskRunData.reportDateStr !== taskRunData.requestedDateStr) ? '(' + taskRunData.reportDateStr + ')' : ''}
                </span>
              </div>
            </div>
          </div>
          <div className='ml-2' style={{ fontSize: '21px', lineHeight: '21px' }}>
            {taskRunData.isDistributedRun ? <i className='icons8-envelope ml-2' /> : null}
            {taskRunData.isScheduledRun ? <i className='icons8-clock ml-2' /> : null}
          </div>
        </div>
        <div>
          <div className='ml-1 status'>Generated {moment(taskRunData.actualRunDateTime).format('ddd, MMM D, YYYY h:mm:ssa')}</div>
        </div>
      </div>
    </li>
  );
}

function RunErrorItem(taskRunData) {
  return (
    <li className='list-group-item lh-120' key={taskRunData.taskRunId}>
      <div className='px-2' key={taskRunData.taskRunId}>
        <div className='d-flex justify-content-between align-items-center'>
          <div className='d-flex justify-content-start align-items-center'>
            <img src={image_error} alt='ERROR' style={{ height: 26 }} />
            <div className='ml-2 d-inline-block align-middle'>
              <span className='text-dark'>
                <span>{taskRunData.reportName}</span>
                <span className='small ml-1'>({taskRunData.configurationName})</span>
              </span>
              <div className='my-1'>
                <span>{taskRunData.requestedDateStr}</span>
                <span className='small ml-1'>
                  {(taskRunData.reportDateStr && taskRunData.reportDateStr.length > 0 && taskRunData.reportDateStr !== taskRunData.requestedDateStr) ? '(' + taskRunData.reportDateStr + ')' : ''}
                </span>
              </div>
            </div>
          </div>
          <div className='ml-2' style={{ fontSize: '21px', lineHeight: '21px' }}>{taskRunData.isScheduledRun ? <i className='icons8-clock' /> : null}</div>
        </div>
        <div>
          <div className='ml-1 mb-1 text-danger status'>{taskRunData.errorMessage}</div>
          <div className='ml-1 status'>Attemped {moment(taskRunData.actualRunDateTime).format('ddd, MMM D, YYYY h:mm:ssa')}</div>
        </div>
      </div>
    </li>
  );
}

function RunProgressItem(props) {
  return (
    <li className='list-group-item lh-120'>
      <div className='px-2'>
        <div className='d-flex justify-content-start align-items-center'>
          <div style={{ lineHeight: '28px' }}>
            <Spinner animation='circular' style={{ height: 24, width: 24, verticalAlign: 'middle' }} />
            <span className='ml-3 font-weight-bold'>In Progress</span>
          </div>
        </div>
        <div>
          <span style={{ display: 'inline-block', width: 24 }}></span>
          <span className='ml-3 status'>
            {props.isCalculating ?
              'Calculating time remaining...'
              : (
                props.secondsUntilComplete > 0
                  ? `Approximate time remaining: ${moment.duration(props.secondsUntilComplete, 'seconds').humanize()}`
                  : 'This may take a few moments...'
              )
            }
          </span>
        </div>
      </div>
    </li>
  );
}